/**
 * @fileoverview Utility Functions for Template Transformations (Vue 3 Compatible)
 *
 * This module provides utility functions for transforming display values.
 * These functions were migrated from Vue 2 global filters to exportable utilities
 * for Vue 3 compatibility.
 *
 * **Available Functions:**
 * - markHTML: Convert markdown to sanitized HTML with styled links
 * - capitalize: Capitalize first letter of a string
 * - time: Timestamp passthrough (identity function)
 *
 * @module filters
 * @author KeyReply Engineering Team
 *
 * @example Usage in Vue 3 templates:
 * ```html
 * <span v-html="markHTML(message, color)"></span>
 * <span>{{ capitalize(name) }}</span>
 * ```
 *
 * @example Usage in script setup:
 * ```typescript
 * import { markHTML, capitalize } from '@/filters';
 *
 * const formattedMessage = computed(() => markHTML(message.value, color.value));
 * const formattedName = computed(() => capitalize(name.value));
 * ```
 *
 * @security
 * - markHTML uses DOMPurify to sanitize HTML and prevent XSS
 * - All links are forced to open in new tabs (target="_blank")
 */

import { marked, Renderer } from "marked";
import dompurify from "dompurify";
import { removeWrapedDiv } from "./helpers/removeWrapDiv";

/**
 * Restrictive DOMPurify config for LLM/bot response rendering (pentest finding GAI-006).
 *
 * Default DOMPurify only strips JavaScript but permits interactive HTML elements
 * (<button>, <form>, <input>) which enables stored HTML injection via LLM output.
 * An attacker can coerce the LLM to include raw HTML that stores in chat history
 * and renders in webchat bubbles or the admin Live Chat monitor.
 *
 * Strategy: allowlist only tags that markdown can legitimately produce so that
 * any LLM-generated HTML element outside this set is stripped to plain text.
 */
const MARKDOWN_SAFE_TAGS = [
  // Block elements
  "p", "br", "hr", "blockquote", "pre",
  // Headings
  "h1", "h2", "h3", "h4", "h5", "h6",
  // Inline formatting
  "strong", "b", "em", "i", "del", "s", "code", "span",
  // Lists
  "ul", "ol", "li",
  // Tables
  "table", "thead", "tbody", "tfoot", "tr", "th", "td",
  // Links and images (from markdown syntax)
  "a", "img",
];

const MARKDOWN_SAFE_ATTRS = [
  "href", "title", "target", "rel",   // links
  "src", "alt", "width", "height",    // images
  "class",                             // class names (no JS)
  "style",                             // inline style for link color (DOMPurify strips JS in CSS)
];

const DOMPURIFY_MARKDOWN_CONFIG: dompurify.Config = {
  ALLOWED_TAGS: MARKDOWN_SAFE_TAGS,
  ALLOWED_ATTR: MARKDOWN_SAFE_ATTRS,
  FORCE_BODY: true,
};

/**
 * Convert markdown text to sanitized HTML
 *
 * This function:
 * 1. Removes wrapper divs from input
 * 2. Parses markdown using marked library with GFM support
 * 3. Customizes link rendering with styled classes and colors
 * 4. Sanitizes output with DOMPurify to prevent XSS attacks
 * 5. Forces all links to open in new tabs
 *
 * @param {string} markdown - Raw markdown text to convert
 * @param {string} color - CSS color value for link styling
 * @returns {string} Sanitized HTML string
 *
 * @security
 * - Uses DOMPurify to sanitize output
 * - Prevents XSS via malicious markdown
 * - Forces target="_blank" on all links
 *
 * @techdebt
 * - DOMPurify hook is added on every call (should be configured once)
 *
 * @example
 * markHTML("**Hello** [World](https://example.com)", "#0066cc")
 * // Returns: "<p><strong>Hello</strong> <a href='https://example.com' class='keyreply-link' ...>World</a></p>"
 */
export function markHTML(markdown = "", color: string): string {
  const renderer = new Renderer();

  // Custom link renderer to add styling and security attributes.
  // marked v9+ passes a token object instead of positional (href, title, text) args.
  renderer.link = ({ href, text }: { href: string; title?: string | null; text: string }) => {
    return `<a href='${href}' class='keyreply-link' title='Click to open link' style='color: ${color};' target='_blank' rel='noopener noreferrer'>${text}</a>`;
  };

  // Remove any wrapper divs that might interfere with parsing
  const cleanMarkdown = removeWrapedDiv(markdown);

  // Parse markdown with GitHub Flavored Markdown support
  const html = marked(cleanMarkdown, {
    renderer,
    gfm: true,   // Enable GitHub Flavored Markdown
    breaks: true, // Convert \n to <br>
  });

  // Configure DOMPurify to force target="_blank" with safe rel on all links.
  // @techdebt This hook is added on every call - should be configured once at init
  dompurify.addHook("afterSanitizeAttributes", function (node) {
    // set non-HTML/MathML links to xlink:show=new
    if (node.hasAttribute("xlink:href") || node.hasAttribute("href")) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  return dompurify.sanitize(html as string, DOMPURIFY_MARKDOWN_CONFIG);
}

/**
 * Timestamp passthrough function (identity function)
 *
 * Currently does nothing - returns the timestamp as-is.
 * Likely a placeholder for future timestamp formatting.
 *
 * @param {any} timestamp - Timestamp value
 * @returns {any} Same timestamp value unchanged
 *
 * @techdebt
 * - This function does nothing useful - consider implementing or removing
 * - Parameter should be typed as Date | string | number
 */
export function time(timestamp: any): any {
  return timestamp;
}

/**
 * Capitalize first letter of a string
 *
 * @param {string} text - String to capitalize
 * @returns {string|false} Capitalized string, or false if not a string
 *
 * @techdebt Return type should be string | undefined, not string | false
 *
 * @example
 * capitalize("hello") // Returns: "Hello"
 * capitalize("WORLD") // Returns: "WORLD"
 * capitalize("")      // Returns: ""
 */
export function capitalize(text: string): string | false {
  return typeof text === "string" && text.charAt(0).toUpperCase() + text.slice(1);
}
